/**
 * Runtime Configuration Service
 * Lädt Konfiguration zur Laufzeit aus config.json
 * Ermöglicht Änderung der API-URL NACH dem Build
 */

export interface AppConfig {
  apiUrl: string;
  onlyofficeUrl: string;
  version: string;
  environment: string;
}

class ConfigService {
  private config: AppConfig | null = null;
  private loading: Promise<AppConfig> | null = null;

  /**
   * Lädt die Konfiguration von /config.json
   * Cached das Ergebnis für nachfolgende Aufrufe
   */
  async loadConfig(): Promise<AppConfig> {
    // Wenn bereits geladen, Cache verwenden
    if (this.config) {
      return this.config;
    }

    // Wenn gerade geladen wird, auf bestehendes Promise warten
    if (this.loading) {
      return this.loading;
    }

    // Config laden
    this.loading = fetch('/config.json', {
      cache: 'no-cache', // Immer frisch laden, nicht cachen
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load config: ${response.statusText}`);
        }
        return response.json();
      })
      .then((config: AppConfig) => {
        // Fallback auf Environment-Variablen wenn config.json fehlt
        if (!config.apiUrl || config.apiUrl === 'BACKEND_URL_HIER_EINTRAGEN') {
          config.apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        }

        if (!config.onlyofficeUrl) {
          config.onlyofficeUrl = process.env.REACT_APP_ONLYOFFICE_SERVER_URL || '';
        }

        this.config = config;
        console.log('✅ Runtime Config loaded:', config);
        return config;
      })
      .catch(error => {
        console.warn('⚠️ Failed to load config.json, using environment variables:', error);

        // Fallback auf Environment-Variablen
        this.config = {
          apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000',
          onlyofficeUrl: process.env.REACT_APP_ONLYOFFICE_SERVER_URL || '',
          version: '1.0.0',
          environment: process.env.NODE_ENV || 'development'
        };

        return this.config;
      })
      .finally(() => {
        this.loading = null;
      });

    return this.loading;
  }

  /**
   * Gibt die API-URL zurück (synchron)
   * Wirft Fehler wenn Config noch nicht geladen wurde
   */
  getApiUrl(): string {
    if (!this.config) {
      throw new Error('Config not loaded yet. Call loadConfig() first.');
    }
    return this.config.apiUrl;
  }

  /**
   * Gibt die OnlyOffice-URL zurück (synchron)
   */
  getOnlyOfficeUrl(): string {
    if (!this.config) {
      throw new Error('Config not loaded yet. Call loadConfig() first.');
    }
    return this.config.onlyofficeUrl;
  }

  /**
   * Gibt die komplette Config zurück (synchron)
   */
  getConfig(): AppConfig {
    if (!this.config) {
      throw new Error('Config not loaded yet. Call loadConfig() first.');
    }
    return this.config;
  }

  /**
   * Prüft ob Config geladen wurde
   */
  isLoaded(): boolean {
    return this.config !== null;
  }
}

// Singleton-Instanz exportieren
export const configService = new ConfigService();

/**
 * Helper-Funktion: Gibt die API-URL zurück
 * Verwendet entweder Runtime-Config oder Environment-Variable als Fallback
 */
export function getApiBaseUrl(): string {
  if (configService.isLoaded()) {
    return configService.getApiUrl();
  }
  // Fallback während Config lädt
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
}

/**
 * Helper-Funktion: Gibt die OnlyOffice-URL zurück
 */
export function getOnlyOfficeUrl(): string {
  if (configService.isLoaded()) {
    return configService.getOnlyOfficeUrl();
  }
  // Fallback während Config lädt
  return process.env.REACT_APP_ONLYOFFICE_SERVER_URL || '';
}
